// Meeting Notes → Tasks AI extractor.
// Paste a paragraph of meeting notes / call summary; AI proposes follow-up tasks
// with title + dueDate + owner + priority + notes. User accepts/rejects each row,
// then commits the kept rows as real tasks.

import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Sparkles, CheckCircle2, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MeetingNotesExtractorDialog({ open, onOpenChange, clientId, onCreated }) {
  const { token } = useAuth();
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState('compose'); // compose → review
  const [extracting, setExtracting] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [proposal, setProposal] = useState(null); // { tasks: [...], advisers: [...], clientName }
  const [rows, setRows] = useState([]); // editable copy with `keep: bool`

  const reset = () => {
    setNotes(''); setProposal(null); setRows([]); setStep('compose');
  };

  const runExtract = async () => {
    if (!notes.trim()) { toast.error('Paste some meeting notes first'); return; }
    setExtracting(true);
    try {
      const res = await fetch(`${API_BASE}/ai/extract-tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId, notes }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'AI could not parse the notes');
        setExtracting(false); return;
      }
      const data = await res.json();
      if (!data.tasks?.length) {
        toast.error('No tasks identified in the notes');
        setExtracting(false); return;
      }
      setProposal(data);
      setRows(data.tasks.map(t => ({ ...t, keep: true })));
      setStep('review');
    } catch { toast.error('Network error'); }
    setExtracting(false);
  };

  const updateRow = (idx, patch) => {
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const commitTasks = async () => {
    const keepers = rows.filter(r => r.keep);
    if (!keepers.length) { toast.error('Select at least one task to create'); return; }
    setCommitting(true);
    try {
      const res = await fetch(`${API_BASE}/ai/extract-tasks/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          clientId,
          tasks: keepers.map(({ keep, ...rest }) => rest), // eslint-disable-line no-unused-vars
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Could not create tasks');
        setCommitting(false); return;
      }
      const data = await res.json();
      toast.success(`${data.created} task${data.created === 1 ? '' : 's'} created`);
      onCreated?.();
      onOpenChange(false);
      setTimeout(reset, 250);
    } catch { toast.error('Network error'); }
    setCommitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setTimeout(reset, 250); }}>
      <DialogContent className="max-w-3xl" data-testid="meeting-extractor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#A86A3A]" />
            Extract follow-up tasks from meeting notes
          </DialogTitle>
          <DialogDescription>
            Paste a meeting summary or call notes. The AI proposes follow-up tasks with owners + due dates pre-filled. Edit, deselect, or keep — then create.
          </DialogDescription>
        </DialogHeader>

        {step === 'compose' && (
          <div className="space-y-3">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Paste meeting notes here…&#10;&#10;e.g. Met with the family this morning. Confirmed Q2 portfolio review for next Friday. Tom to send updated SOA by Wednesday. Need to update KYC docs for the SMSF urgently."
              className="min-h-[220px] font-mono text-sm"
              data-testid="meeting-notes-textarea"
            />
            <p className="text-xs text-[#8C8078]">
              Tip: include who said what, any dates mentioned, and any specific owner names — the AI uses those to assign the task.
            </p>
          </div>
        )}

        {step === 'review' && proposal && (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-[#F9F7F5] border border-[#E6E0D9] rounded-md px-3 py-2">
              <div className="text-xs text-[#5D524D]">
                <strong>{proposal.clientName}</strong> · {rows.filter(r => r.keep).length} of {rows.length} task(s) selected
              </div>
              <Button size="sm" variant="ghost" onClick={() => setStep('compose')} data-testid="back-to-notes-btn">
                Edit notes
              </Button>
            </div>

            <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className={`border rounded-md p-3 space-y-2 ${r.keep ? 'border-[#E6E0D9] bg-white' : 'border-[#E6E0D9] bg-stone-50 opacity-50'}`}
                  data-testid={`extracted-task-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <button
                      onClick={() => updateRow(i, { keep: !r.keep })}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${r.keep ? 'bg-[#4A3B32] border-[#4A3B32]' : 'border-[#D4CCC6]'}`}
                      data-testid={`toggle-task-${i}`}
                    >
                      {r.keep && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </button>
                    <Input
                      value={r.title}
                      onChange={(e) => updateRow(i, { title: e.target.value })}
                      className="flex-1 h-8 text-sm"
                      data-testid={`task-title-${i}`}
                    />
                    <button
                      onClick={() => updateRow(i, { keep: false })}
                      className="text-[#8C8078] hover:text-red-600 p-1"
                      title="Drop this task"
                      data-testid={`drop-task-${i}`}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pl-7">
                    <Input
                      type="date"
                      value={r.dueDate || ''}
                      onChange={(e) => updateRow(i, { dueDate: e.target.value })}
                      className="h-8 text-xs"
                      data-testid={`task-due-${i}`}
                    />
                    <Select value={r.owner || '__unassigned'} onValueChange={(v) => updateRow(i, { owner: v === '__unassigned' ? null : v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`task-owner-${i}`}><SelectValue placeholder="Owner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned">Unassigned</SelectItem>
                        {(proposal.advisers || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={r.priority} onValueChange={(v) => updateRow(i, { priority: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`task-priority-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {r.notes && <p className="text-[11px] text-[#5D524D] pl-7 italic">{r.notes}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'compose' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={runExtract} disabled={!notes.trim() || extracting} data-testid="run-extract-btn">
                {extracting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Extract tasks
              </Button>
            </>
          )}
          {step === 'review' && (
            <>
              <Button variant="outline" onClick={() => setStep('compose')}>Back</Button>
              <Button onClick={commitTasks} disabled={committing || rows.filter(r => r.keep).length === 0} data-testid="commit-extracted-btn">
                {committing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Create {rows.filter(r => r.keep).length} task{rows.filter(r => r.keep).length === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
